import { Alert } from "@mui/lab";
import { AppBar, Button, Card, CardContent, FormControlLabel, FormHelperText, Switch, TextField, Toolbar } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { FormStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonCancel from "components/Buttons/ButtonCancel";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import FormSubtitle from "components/FormSubtitle";
import LightboxReact from "components/LightBox/react-lightbox";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { RecordAbout } from "components/RecordAbout";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import IntegrationQueueEventName from "components/Translations/IntegrationQueueEventName";
import { Formaters } from "lib/Formarters";
import LibUtils from "lib/LibUtils";
import { GetSetupIntegrationDto, SaveSetupIntegrationDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { Prompt } from "react-router";
import SetupIntegrationLogDialog from "views/System/Components/SetupIntegrationLogDialog";

/////////////////////////////////////////////////////////////////////////////////////

export interface ISetupProps {
    onEditing: (editing: boolean) => void;
}

interface IProps {
}

interface IState {
    dialog: any;
    helpExpanded1: boolean;
    helpExpanded2: boolean;
    helpExpanded3: boolean;
    helpExpanded4: boolean;
    helpExpanded5: boolean;
    helpExpanded6: boolean;
    helpExpanded7: boolean;
    helpExpanded8: boolean;
    formCycle: RequestLifeCycle;
    lastError: any;
    record: GetSetupIntegrationDto;
    samlCertificate: string;
    validate: {
        azTenantId: boolean;
        azPrimaryDomain: boolean;
        samlLoginUrl: boolean;
        samlLogoutUrl: boolean;
        samlIdentifier: boolean;
        samlCertificate: boolean;
        applicationId: boolean,
        applicationObjectId: boolean,
        applicationSecret: boolean,
        applicationMsalUrl: boolean;
        acceptSiteSsoFromUri: boolean;
        siteSingleSignOnKey: boolean;
        eventIntegrationUrl: boolean;
    };
}

type Props = ISetupProps & IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class SetupIntegrationEdit extends React.Component<Props, IState> {

    private compareDto: any = null;
    private samlCreated = false;
    private uploadNewCertificate = false;

    ///
    constructor(props: Props) {
        super(props);

        this.compareDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            lastError: null,
            dialog: null,
            helpExpanded1: false,
            helpExpanded2: false,
            helpExpanded3: false,
            helpExpanded4: false,
            helpExpanded5: false,
            helpExpanded6: false,
            helpExpanded7: false,
            helpExpanded8: false,
            samlCertificate: null,
            record: {
                enableAzEnterpriseAppSso: false,
                azPrimaryDomain: null,
                azTenantId: null,
                applicationId: null,
                applicationObjectId: null,
                applicationSecret: null,
                applicationMsalUrl: null,

                enableSAML: false,
                samlIdentifier: null,
                samlLoginUrl: null,
                samlLogoutUrl: null,
                samlCertificateLoaded: false,
                objectIdClaimField: null,
                firstNameClaimField: null,
                lastNameClaimField: null,
                emailClaimField: null,
                locationClaimField: null,
                phoneClaimField: null,

                enableSiteSso: false,
                acceptSiteSsoFromUri: null,
                siteSingleSignOnKey: null,

                enableEventIntegration: false,
                eventIntegrationKey: null,
                eventIntegrationUrl: null,
                eventIntegrationInfo: null,

                recordInfo: null,
            },
            validate: {
                azTenantId: null,
                azPrimaryDomain: null,
                samlIdentifier: null,
                samlLoginUrl: null,
                samlLogoutUrl: null,
                samlCertificate: null,
                applicationId: null,
                applicationObjectId: null,
                applicationSecret: null,
                applicationMsalUrl: null,
                acceptSiteSsoFromUri: null,
                siteSingleSignOnKey: null,
                eventIntegrationUrl: null
            },
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.System.Components.SetupIntegrationEdit." + r, f);
    }

    ///
    private load() {
        api.getSetupIntegration()
            .then((dto) => {
                const isNew = dto == null;
                if (dto == null) dto = this.state.record;

                this.uploadNewCertificate = false;
                this.compareDto = LibUtils.deepCopy(dto);
                this.samlCreated = dto.enableAzEnterpriseAppSso && dto.applicationId != null;
                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    record: dto,
                    validate: {
                        azTenantId: isNew ? null : dto.azTenantId != null,
                        azPrimaryDomain: isNew ? null : dto.azPrimaryDomain != null,
                        samlLoginUrl: isNew ? null : dto.samlLoginUrl != null,
                        samlLogoutUrl: isNew ? null : dto.samlLogoutUrl != null,
                        samlIdentifier: isNew ? null : dto.samlIdentifier != null,
                        samlCertificate: isNew ? null : dto.samlCertificateLoaded,
                        applicationId: isNew ? null : dto.applicationId != null,
                        applicationObjectId: isNew ? null : dto.applicationObjectId != null,
                        applicationSecret: isNew ? null : dto.applicationSecret != null,
                        applicationMsalUrl: isNew ? null : dto.applicationMsalUrl != null,
                        acceptSiteSsoFromUri: isNew ? null : dto.acceptSiteSsoFromUri != null,
                        siteSingleSignOnKey: isNew ? null : dto.siteSingleSignOnKey != null,
                        eventIntegrationUrl: isNew ? null : dto.eventIntegrationUrl != null,
                    },
                }, () => this.props.onEditing(false));
            })
            .catch((err: any) => {
                this.setState({
                    lastError: err,
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private getPostDto(): SaveSetupIntegrationDto {
        const { record } = this.state;

        return {
            enableAzEnterpriseAppSso: record.enableSAML && record.enableAzEnterpriseAppSso,
            azPrimaryDomain: LibUtils.emptyToNull(record.azPrimaryDomain),
            azTenantId: LibUtils.emptyToNull(record.azTenantId),
            applicationId: LibUtils.emptyToNull(record.applicationId),
            applicationObjectId: LibUtils.emptyToNull(record.applicationObjectId),
            applicationSecret: LibUtils.emptyToNull(record.applicationSecret),
            applicationMsalUrl: LibUtils.emptyToNull(record.applicationMsalUrl),

            enableSAML: record.enableSAML,
            samlIdentifier: LibUtils.emptyToNull(record.samlIdentifier),
            samlLoginUrl: LibUtils.emptyToNull(record.samlLoginUrl),
            samlLogoutUrl: LibUtils.emptyToNull(record.samlLogoutUrl),
            samlCertificate: this.state.samlCertificate,
            objectIdClaimField: LibUtils.emptyToNull(record.objectIdClaimField),
            locationClaimField: LibUtils.emptyToNull(record.locationClaimField),
            phoneClaimField: LibUtils.emptyToNull(record.phoneClaimField),
            emailClaimField: LibUtils.emptyToNull(record.emailClaimField),
            firstNameClaimField: LibUtils.emptyToNull(record.firstNameClaimField),
            lastNameClaimField: LibUtils.emptyToNull(record.lastNameClaimField),

            enableSiteSso: record.enableSiteSso,
            acceptSiteSsoFromUri: LibUtils.emptyToNull(record.acceptSiteSsoFromUri),
            siteSingleSignOnKey: LibUtils.emptyToNull(record.siteSingleSignOnKey),

            enableEventIntegration: record.enableEventIntegration,
            eventIntegrationKey: LibUtils.emptyToNull(record.eventIntegrationKey),
            eventIntegrationUrl: LibUtils.emptyToNull(record.eventIntegrationUrl)
        };
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {

            var newState = { ...state };

            if (name == "samlCertificate") {
                var certText = (v as string);
                newState.samlCertificate = certText;
                newState.validate[name] = certText.startsWith("-----BEGIN CERTIFICATE-----");
                newState.record.samlCertificateLoaded = true;
                this.uploadNewCertificate = true;
            }
            else {
                newState.record[name] = v;

                if (newState.validate.hasOwnProperty(name)) {
                    newState.validate[name] = v != "";
                }
            }

            return newState;
        }, () => this.props.onEditing(this.isRecordChanged()));
    }

    ///
    private formIsValid() {
        const { record, validate } = this.state;

        if (record.enableAzEnterpriseAppSso &&
            (!validate.azPrimaryDomain
                || !validate.azTenantId
                || !validate.applicationId
                || !validate.applicationObjectId
                || !validate.applicationSecret
                || !validate.applicationMsalUrl
            )
        )
            return false;

        if (record.enableSAML &&
            (!validate.samlCertificate
                || !validate.samlIdentifier
                || !validate.samlLoginUrl
            )
        )
            return false;

        if (record.enableSiteSso && !validate.acceptSiteSsoFromUri)
            return false;

        if (record.enableEventIntegration && !validate.eventIntegrationUrl)
            return false;

        return true;
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (!this.formIsValid())
            return;

        const dto = this.getPostDto();

        this.setState({
            formCycle: RequestLifeCycle.Posting,
        });

        api.saveSetupIntegration(dto)
            .then((response) => {
                this.uploadNewCertificate = false;
                this.compareDto = LibUtils.deepCopy(this.state.record);
                this.props.snackbar.showMessage(this.props.t("Common.Literals.UpdateSuccess"), "success");

                this.setState({ formCycle: RequestLifeCycle.PostSuccess }, () => this.props.onEditing(false));
            })
            .catch((err: any) => {
                this.setState({ lastError: err, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private handleCertificate = (e: any) => {
        const reader = new FileReader();
        const file = e.target.files[0];
        if (file) {
            reader.onloadend = () => {
                if (reader.result) {
                    this.updateRecord("samlCertificate", reader.result);
                }
            };
            reader.readAsText(file);
        }
    };

    ///
    private isRecordChanged() {
        return this.state.lastError == null && (!LibUtils.deepCompare(this.state.record, this.compareDto) || this.uploadNewCertificate);
    }

    ///
    private renderToolbar() {
        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}  >
                <Toolbar>
                    <div className={GlobalClassKeys.pushRight}>
                        {
                            this.isRecordChanged() &&
                            <ButtonCancel
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.load()}
                            />
                        }
                        <ButtonSave
                            disabled={!this.formIsValid()}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                        />

                    </div>
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <RecordAbout
                        record={this.state.record.recordInfo}
                        hideCreator={true}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <SetupIntegrationLogDialog
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderAzEnterpriseAppSso() {
        const { record, validate } = this.state;

        if (!record.enableSAML)
            return null;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor
                        color="primary"
                        title={this.res("EnterpriseAppSsoTitle")}
                        subheader={(
                            <MuiMuted>
                                <small>
                                    {this.res("EnterpriseAppSsoSubtitle1")}
                                    <br />
                                    {this.res("EnterpriseAppSsoSubtitle2")}
                                </small>
                            </MuiMuted>
                        )}
                    />
                    <CardContent>

                        <MuiContainer margin="normal">
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="enableAzEnterpriseAppSso"
                                        checked={record.enableAzEnterpriseAppSso === true}
                                        onChange={this.handleChange}
                                        value="enableAzEnterpriseAppSso"
                                        color="secondary"
                                    />
                                }
                                label={this.res("EnableOptionLabel")}
                            />
                        </MuiContainer>

                        {
                            record.enableAzEnterpriseAppSso &&
                            <>
                                <MuiContainer margin="normal">
                                    <FormSubtitle text="Azure Active Directory > Overview" />
                                    <Grid container spacing={2}>
                                        <Grid item xs>
                                            <TextField
                                                name="azTenantId"
                                                label={this.res("AzTenantIdLabel")}
                                                autoComplete="off"
                                                error={validate.azTenantId === false}
                                                margin="none"
                                                value={record.azTenantId || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 50 }}
                                                fullWidth
                                                required
                                            />

                                            <TextField
                                                name="azPrimaryDomain"
                                                label={this.res("AzPrimaryDomainLabel")}
                                                autoComplete="off"
                                                error={validate.azPrimaryDomain === false}
                                                margin="normal"
                                                value={record.azPrimaryDomain || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 50 }}
                                                fullWidth
                                                required
                                            />
                                        </Grid>
                                        <Grid item>
                                            <MuiClickable onClick={() => this.setState({ helpExpanded1: true })}>
                                                <img
                                                    src="/img/app/AzureSSOTenant.png"
                                                    alt="Azure Tenant Configuration"
                                                    style={{ width: 128 }}
                                                />
                                            </MuiClickable>
                                            {
                                                this.state.helpExpanded1 &&
                                                <LightboxReact
                                                    mainSrc="/img/app/AzureSSOTenant.png"
                                                    onCloseRequest={() => this.setState({ helpExpanded1: false })}
                                                />
                                            }
                                        </Grid>
                                    </Grid>

                                </MuiContainer>

                                <MuiContainer margin="normal">
                                    <Grid container spacing={2}>
                                        <Grid item xs >

                                            <FormSubtitle text="Enterprise Application > Overview" />

                                            <TextField
                                                name="applicationId"
                                                label={this.res("ApplicationId")}
                                                autoComplete="off"
                                                error={validate.applicationId === false}
                                                margin="none"
                                                value={record.applicationId || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                fullWidth
                                                required
                                            />

                                            <TextField
                                                name="applicationObjectId"
                                                label={this.res("ApplicationObjectId")}
                                                autoComplete="off"
                                                error={validate.applicationObjectId === false}
                                                margin="normal"
                                                value={record.applicationObjectId || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                fullWidth
                                                required
                                            />

                                        </Grid>

                                        <Grid item>
                                            <MuiClickable onClick={() => this.setState({ helpExpanded2: true })}>
                                                <img
                                                    src="/img/app/AzureEnterpriseAppOverview.png"
                                                    alt="Azure SSO Configuration"
                                                    style={{ width: 128 }}
                                                />
                                            </MuiClickable>
                                            {
                                                this.state.helpExpanded2 &&
                                                <LightboxReact
                                                    mainSrc="/img/app/AzureEnterpriseAppOverview.png"
                                                    onCloseRequest={() => this.setState({ helpExpanded2: false })}
                                                />
                                            }
                                        </Grid>

                                    </Grid>
                                </MuiContainer>

                                <MuiContainer margin="normal">
                                    <Grid container spacing={2}>
                                        <Grid item xs >

                                            <FormSubtitle text="App Registration > Certificates & Secrets > Client Secrets" />

                                            <TextField
                                                name="applicationSecret"
                                                label={this.res("ApplicationSecret")}
                                                autoComplete="off"
                                                error={validate.applicationSecret === false}
                                                margin="none"
                                                value={record.applicationSecret || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                fullWidth
                                                required
                                            />

                                        </Grid>
                                        <Grid item>

                                            <MuiClickable onClick={() => this.setState({ helpExpanded8: true })}>
                                                <img
                                                    src="/img/app/AzureAppClientSecrets.png"
                                                    alt="Azure SSO Configuration"
                                                    style={{ width: 128 }}
                                                />
                                            </MuiClickable>
                                            {
                                                this.state.helpExpanded8 &&
                                                <LightboxReact
                                                    mainSrc="/img/app/AzureAppClientSecrets.png"
                                                    onCloseRequest={() => this.setState({ helpExpanded8: false })}
                                                />
                                            }

                                        </Grid>
                                    </Grid>
                                </MuiContainer>

                                <MuiContainer margin="normal">
                                    <Grid container spacing={2}>
                                        <Grid item xs >

                                            <FormSubtitle text="App Registration > Authentication > Mobile and Desktop Applications" />

                                            <TextField
                                                name="applicationMsalUrl"
                                                label={this.res("ApplicationMsalUrlLabel")}
                                                autoComplete="off"
                                                error={validate.applicationMsalUrl === false}
                                                margin="none"
                                                value={record.applicationMsalUrl || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                fullWidth
                                                required
                                            />
                                            <FormHelperText>{this.res("ApplicationMsalUrlHelpText")}</FormHelperText>

                                        </Grid>
                                        <Grid item>

                                            <MuiClickable onClick={() => this.setState({ helpExpanded7: true })}>
                                                <img
                                                    src="/img/app/AzureAppMobile.png"
                                                    alt="Azure SSO Configuration"
                                                    style={{ width: 128 }}
                                                />
                                            </MuiClickable>
                                            {
                                                this.state.helpExpanded7 &&
                                                <LightboxReact
                                                    mainSrc="/img/app/AzureAppMobile.png"
                                                    onCloseRequest={() => this.setState({ helpExpanded7: false })}
                                                />
                                            }

                                        </Grid>
                                    </Grid>
                                </MuiContainer>
                            </>
                        }

                    </CardContent>
                </Card >

            </MuiContainer >
        );
    }

    private renderSAML() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor
                        color="primary"
                        title={this.res("SAMLTitle")}
                        subheader={(
                            <MuiMuted>
                                <small>
                                    {this.res("SAMLSubtitle")}
                                </small>
                            </MuiMuted>
                        )}
                    />
                    <CardContent>

                        <MuiContainer margin="normal">
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="enableSAML"
                                        checked={record.enableSAML === true}
                                        onChange={this.handleChange}
                                        value="enableSAML"
                                        color="secondary"
                                    />
                                }
                                label={this.res("EnableOptionLabel")}
                            />
                        </MuiContainer>

                        {
                            record.enableSAML &&
                            <>
                                <MuiContainer margin="normal">
                                    <Grid container spacing={2}>
                                        <Grid item xs>
                                            <FormSubtitle text={this.res("SAMLBasicSubtitle")} />

                                            <TextField
                                                name="samlIdentifier"
                                                label={this.res("SamlIdentifierLabel")}
                                                autoComplete="off"
                                                error={validate.samlIdentifier === false}
                                                margin="none"
                                                value={record.samlIdentifier || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                fullWidth
                                                required
                                            />

                                        </Grid>
                                        <Grid item>
                                            <MuiClickable onClick={() => this.setState({ helpExpanded3: true })}>
                                                <img
                                                    src="/img/app/AzureEnterpriseAppSSO1.png"
                                                    alt="Azure SSO Configuration"
                                                    style={{ width: 128 }}
                                                />
                                            </MuiClickable>
                                            {
                                                this.state.helpExpanded3 &&
                                                <LightboxReact
                                                    mainSrc="/img/app/AzureEnterpriseAppSSO1.png"
                                                    onCloseRequest={() => this.setState({ helpExpanded3: false })}
                                                />
                                            }
                                        </Grid>
                                    </Grid>

                                    <Grid container>
                                        <Grid item xs>
                                            <TextField
                                                name="samlLoginUrl"
                                                label={this.res("SamlLoginUrlLabel")}
                                                autoComplete="off"
                                                error={validate.samlLoginUrl === false}
                                                margin="normal"
                                                value={record.samlLoginUrl || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                fullWidth
                                                required
                                            />

                                            <TextField
                                                name="samlLogoutUrl"
                                                label={this.res("SamlLogoutUrlLabel")}
                                                autoComplete="off"
                                                error={validate.samlLogoutUrl === false}
                                                margin="normal"
                                                value={record.samlLogoutUrl || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item>
                                            <MuiClickable onClick={() => this.setState({ helpExpanded6: true })}>
                                                <img
                                                    src="/img/app/AzureEnterpriseAppSSO4.png"
                                                    alt="Azure SSO Configuration"
                                                    style={{ width: 128 }}
                                                />
                                            </MuiClickable>
                                            {
                                                this.state.helpExpanded6 &&
                                                <LightboxReact
                                                    mainSrc="/img/app/AzureEnterpriseAppSSO4.png"
                                                    onCloseRequest={() => this.setState({ helpExpanded6: false })}
                                                />
                                            }
                                        </Grid>
                                    </Grid>

                                </MuiContainer>

                                <MuiContainer margin="normal">
                                    <Grid container spacing={2}>
                                        <Grid item xs >
                                            <FormSubtitle text={this.res("SAMLClaimsSubtitle")} />

                                            <TextField
                                                name="objectIdClaimField"
                                                label={this.res("ObjectIdClaimFieldLabel")}
                                                autoComplete="off"
                                                margin="none"
                                                value={record.objectIdClaimField || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder={"http://schemas.microsoft.com/identity/claims/objectidentifier"}
                                                fullWidth
                                            />

                                            <TextField
                                                name="firstNameClaimField"
                                                label={this.res("FirstNameClaimFieldLabel")}
                                                autoComplete="off"
                                                margin="none"
                                                value={record.firstNameClaimField || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder={"first_name | http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"}
                                                fullWidth
                                            />

                                            <TextField
                                                name="lastNameClaimField"
                                                label={this.res("LastNameClaimFieldLabel")}
                                                autoComplete="off"
                                                margin="none"
                                                value={record.lastNameClaimField || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder={"last_name | http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"}
                                                fullWidth
                                            />

                                            <TextField
                                                name="emailClaimField"
                                                label={this.res("EmailClaimFieldLabel")}
                                                autoComplete="off"
                                                margin="none"
                                                value={record.emailClaimField || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder={"User.email | http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"}
                                                fullWidth
                                            />

                                            <TextField
                                                name="locationClaimField"
                                                label={this.res("LocationClaimFieldLabel")}
                                                autoComplete="off"
                                                margin="none"
                                                value={record.locationClaimField || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder={"http://schemas.xmlsoap.org/ws/2005/05/identity/claims/locality"}
                                                fullWidth
                                            />

                                            <TextField
                                                name="phoneClaimField"
                                                label={this.res("PhoneClaimFieldLabel")}
                                                autoComplete="off"
                                                margin="normal"
                                                value={record.phoneClaimField || ""}
                                                onChange={this.handleChange}
                                                inputProps={{ maxLength: 255 }}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder={"http://schemas.xmlsoap.org/ws/2005/05/identity/claims/telephonenumber"}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item>
                                            <MuiClickable onClick={() => this.setState({ helpExpanded4: true })}>
                                                <img
                                                    src="/img/app/AzureEnterpriseAppSSO2.png"
                                                    alt="Azure SSO Configuration"
                                                    style={{ width: 128 }}
                                                />
                                            </MuiClickable>
                                            {
                                                this.state.helpExpanded4 &&
                                                <LightboxReact
                                                    mainSrc="/img/app/AzureEnterpriseAppSSO2.png"
                                                    onCloseRequest={() => this.setState({ helpExpanded4: false })}
                                                />
                                            }
                                        </Grid>
                                    </Grid>
                                </MuiContainer>

                                <MuiContainer margin="normal">
                                    <Grid container>
                                        <Grid item xs>
                                            <FormSubtitle text={this.res("SAMLCertificateSubtitle")} />
                                            <TextField
                                                name="samlCertificate"
                                                label={this.res("SamlCertificateLabel")}
                                                autoComplete="off"
                                                type="file"
                                                margin="normal"
                                                fullWidth
                                                required={record.enableAzEnterpriseAppSso && !this.samlCreated}
                                                inputProps={
                                                    {
                                                        onChange: this.handleCertificate
                                                    }
                                                }
                                                InputLabelProps={
                                                    {
                                                        shrink: true
                                                    }
                                                }
                                            />
                                            {
                                                validate.samlCertificate === false &&
                                                <FormHelperText error>{this.res("InvalidCertificateText")}</FormHelperText>
                                            }

                                        </Grid>
                                        <Grid item>
                                            <MuiClickable onClick={() => this.setState({ helpExpanded5: true })}>
                                                <img
                                                    src="/img/app/AzureEnterpriseAppSSO3.png"
                                                    alt="Azure SSO Configuration"
                                                    style={{ width: 128 }}
                                                />
                                            </MuiClickable>
                                            {
                                                this.state.helpExpanded5 &&
                                                <LightboxReact
                                                    mainSrc="/img/app/AzureEnterpriseAppSSO3.png"
                                                    onCloseRequest={() => this.setState({ helpExpanded5: false })}
                                                />
                                            }
                                        </Grid>
                                    </Grid>
                                </MuiContainer>

                                <MuiContainer margin="normal">

                                </MuiContainer>
                            </>
                        }

                    </CardContent>
                </Card >

            </MuiContainer >
        );
    }

    ///
    private renderSiteSso() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("SiteSsoTitle")} />
                    <CardContent>

                        <FormControlLabel
                            control={
                                <Switch
                                    name="enableSiteSso"
                                    checked={record.enableSiteSso === true}
                                    onChange={this.handleChange}
                                    value="enableSiteSso"
                                    color="secondary"
                                />
                            }
                            label={this.res("EnableOptionLabel")}
                        />

                        {
                            record.enableSiteSso &&
                            <>
                                <MuiContainer margin="normal">
                                    <TextField
                                        name="acceptSiteSsoFromUri"
                                        label={this.res("AcceptSiteSsoFromUriLabel")}
                                        autoComplete="off"
                                        error={validate.acceptSiteSsoFromUri === false}
                                        value={record.acceptSiteSsoFromUri || ""}
                                        onChange={this.handleChange}
                                        inputProps={{ maxLength: 255 }}
                                        fullWidth
                                        required
                                    />
                                    <FormHelperText>{this.res("SiteSsoFromUriPlaceholder")}</FormHelperText>
                                </MuiContainer>

                                <MuiContainer margin="normal">
                                    <Grid container alignItems="flex-end">
                                        <Grid item xs>
                                            <TextField
                                                name="siteSingleSignOnKey"
                                                label={this.res("SiteSignOnKeyLabel")}
                                                value={record.siteSingleSignOnKey}
                                                fullWidth
                                                inputProps={{ maxLength: 64 }}
                                                onChange={this.handleChange}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                onClick={
                                                    () => {
                                                        this.updateRecord("siteSingleSignOnKey", LibUtils.newGuid());
                                                    }
                                                }
                                            >
                                                <span>{this.res("GenerateKeyText")}</span>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <FormHelperText>{this.res("SiteSignOnKeyPlaceholder")}</FormHelperText>
                                </MuiContainer>
                            </>
                        }

                    </CardContent>
                </Card>

            </MuiContainer>
        );
    }

    ///
    private renderEventIntegration() {
        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal">

                <Card>
                    <CardHeaderColor color="primary" title={this.res("EventIntegrationTitle")} />
                    <CardContent>

                        <FormControlLabel
                            control={
                                <Switch
                                    name="enableEventIntegration"
                                    checked={record.enableEventIntegration === true}
                                    onChange={this.handleChange}
                                    value="enableEventIntegration"
                                    color="secondary"
                                />
                            }
                            label={this.res("EnableOptionLabel")}
                        />

                        {
                            record.enableEventIntegration &&
                            <>
                                <MuiContainer margin="normal">
                                    <TextField
                                        name="eventIntegrationUrl"
                                        label={this.res("EventIntegrationUrlLabel")}
                                        autoComplete="off"
                                        error={validate.eventIntegrationUrl === false}
                                        value={record.eventIntegrationUrl || ""}
                                        onChange={this.handleChange}
                                        inputProps={{ maxLength: 255 }}
                                        fullWidth
                                        required
                                    />
                                    <FormHelperText>{this.res("EventIntegrationUrlPlaceholder")}</FormHelperText>
                                </MuiContainer>

                                <MuiContainer margin="normal">
                                    <TextField
                                        name="eventIntegrationKey"
                                        label={this.res("EventIntegrationKeyLabel")}
                                        value={record.eventIntegrationKey}
                                        fullWidth
                                        inputProps={{ maxLength: 64 }}
                                        onChange={this.handleChange}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <FormHelperText>{this.res("EventIntegrationKeyPlaceholder")}</FormHelperText>
                                </MuiContainer>

                                {
                                    record.eventIntegrationInfo?.queueCount == 0
                                        ? <Alert severity="info">{this.res("EventQueueEmpty")}</Alert>
                                        : record.eventIntegrationInfo?.queueCount > 0
                                            ? (
                                                <Alert severity={record.eventIntegrationInfo.lastStatusCode == null ? "success" : "error"}>
                                                    <table>
                                                        <tr>
                                                            <td>
                                                                <div><b>{this.res(record.eventIntegrationInfo.lastStatusCode == null ? "EventQueueNextLabel" : "EventQueueLastLabel")}:</b></div>
                                                                <div><IntegrationQueueEventName event={record.eventIntegrationInfo.eventId} /></div>
                                                                <div>{Formaters.localeDatetime(record.eventIntegrationInfo.postedOn)}</div>
                                                                {
                                                                    record.eventIntegrationInfo.queueCount > 1 &&
                                                                    <div>{this.res("EventQueueCount", { count: record.eventIntegrationInfo.queueCount - 1 })}</div>
                                                                }
                                                            </td>
                                                        </tr>
                                                        {
                                                            record.eventIntegrationInfo.lastStatusCode != null &&
                                                            <tr>
                                                                <td>
                                                                    <div><b>{this.res("LastEventStatusCode")}:</b></div>
                                                                    <div><b>{record.eventIntegrationInfo.lastStatusCode}</b></div>
                                                                    <div>{record.eventIntegrationInfo.lastStatus}</div>

                                                                    {
                                                                        record.eventIntegrationInfo.lastProcessingDate &&
                                                                        <>
                                                                            <div><b>{this.res("LastEventSent")}:</b></div>
                                                                            <div>{Formaters.localeDatetime(record.eventIntegrationInfo.lastProcessingDate)}</div>
                                                                        </>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        }
                                                    </table>
                                                </Alert>
                                            )
                                            : null
                                }
                            </>
                        }
                    </CardContent>
                </Card>

            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    public render() {

        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        if (this.state.formCycle == RequestLifeCycle.InitFailed) {
            return <RequestErrorReport error={this.state.lastError} />;
        }

        return (
            <Grid container justifyContent="space-around">
                <Grid item xs={12} md={10} lg={8}>

                    {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                    <RequestErrorDialog
                        open={this.state.formCycle == RequestLifeCycle.PostFailed}
                        error={this.state.lastError}
                        onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                    />

                    {this.state.dialog}

                    {this.renderToolbar()}

                    <form noValidate autoComplete="nope" >
                        {this.renderSAML()}
                        {this.renderAzEnterpriseAppSso()}
                        {this.renderSiteSso()}
                        {this.renderEventIntegration()}
                    </form>

                    {this.renderRecordInfo()}

                    <Prompt
                        when={this.isRecordChanged()}
                        message={this.props.t("Common.Question.Unsave")}
                    />
                </Grid>
            </Grid>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(SetupIntegrationEdit)));